import { useState, useEffect } from 'react';
import { hoyEnChile } from '../utils/dateHelpers';
import {
  CategoriaEgreso,
  getSubcategoriasPorCategoria,
  getReembolsoPillClass,
  computeGastoPillClasses,
} from '../config/gastos';

export interface GastoFormState {
  readonly categoriaEgreso: CategoriaEgreso;
  readonly setCategoriaEgreso: (v: CategoriaEgreso) => void;
  readonly subcategoria: string;
  readonly setSubcategoria: (v: string) => void;
  readonly otraSubcategoria: string;
  readonly setOtraSubcategoria: (v: string) => void;
  readonly monto: string;
  readonly setMonto: (v: string) => void;
  readonly fecha: string;
  readonly setFecha: (v: string) => void;
  readonly descripcion: string;
  readonly setDescripcion: (v: string) => void;
  readonly requiereReembolso: boolean;
  readonly setRequiereReembolso: (v: boolean) => void;
  readonly reembolsoTipo: 'miembro' | 'externo';
  readonly setReembolsoTipo: (v: 'miembro' | 'externo') => void;
  readonly miembroReembolsoId: string;
  readonly setMiembroReembolsoId: (v: string) => void;
  readonly nombreExternoReembolso: string;
  readonly setNombreExternoReembolso: (v: string) => void;
  readonly esDonacion: boolean;
  readonly donanteTipo: 'miembro' | 'externo';
  readonly setDonanteTipo: (v: 'miembro' | 'externo') => void;
  readonly miembroDonante: string;
  readonly setMiembroDonante: (v: string) => void;
  readonly nombreExternoDonante: string;
  readonly setNombreExternoDonante: (v: string) => void;
  // computed
  readonly subcategorias: readonly string[];
  readonly reembolsoClassName: string;
  readonly pillClasses: ReturnType<typeof computeGastoPillClasses>;
  // handlers
  readonly handleDonacionToggle: () => void;
  readonly validateForm: () => string | null;
  readonly resetForm: () => void;
  readonly hasFormChanges: () => boolean;
}

export function useGastoForm(): GastoFormState {
  const [categoriaEgreso, setCategoriaEgreso] = useState<CategoriaEgreso>('general');
  const [subcategoria, setSubcategoria] = useState('');
  const [otraSubcategoria, setOtraSubcategoria] = useState('');
  const [monto, setMonto] = useState('');
  const [fecha, setFecha] = useState(() => hoyEnChile());
  const [descripcion, setDescripcion] = useState('');
  const [requiereReembolso, setRequiereReembolso] = useState(false);
  const [reembolsoTipo, setReembolsoTipo] = useState<'miembro' | 'externo'>('miembro');
  const [miembroReembolsoId, setMiembroReembolsoId] = useState('');
  const [nombreExternoReembolso, setNombreExternoReembolso] = useState('');
  const [esDonacion, setEsDonacion] = useState(false);
  const [donanteTipo, setDonanteTipo] = useState<'miembro' | 'externo'>('miembro');
  const [miembroDonante, setMiembroDonante] = useState('');
  const [nombreExternoDonante, setNombreExternoDonante] = useState('');

  useEffect(() => {
    setSubcategoria('');
    setOtraSubcategoria('');
  }, [categoriaEgreso]);

  const subcategorias = getSubcategoriasPorCategoria(categoriaEgreso);
  const reembolsoClassName = getReembolsoPillClass(esDonacion, requiereReembolso);
  const pillClasses = computeGastoPillClasses(esDonacion, requiereReembolso);

  const handleDonacionToggle = () => {
    const nuevaEsDonacion = !esDonacion;
    setEsDonacion(nuevaEsDonacion);
    if (nuevaEsDonacion) {
      setRequiereReembolso(false);
      setMiembroReembolsoId('');
      setNombreExternoReembolso('');
    }
  };

  const validateForm = (): string | null => {
    if (!categoriaEgreso) return 'Por favor selecciona una categoría de gasto';
    if (!subcategoria) return 'Por favor selecciona una subcategoría';
    if (subcategoria === 'Otra' && !otraSubcategoria.trim()) return 'Por favor especifica la subcategoría';
    if (!monto || Number.parseFloat(monto) <= 0) return 'Por favor ingresa un monto válido';
    if (!descripcion.trim()) return 'Por favor ingresa una descripción del gasto';
    if (requiereReembolso && reembolsoTipo === 'miembro' && !miembroReembolsoId) return 'Por favor selecciona el miembro a reembolsar';
    if (requiereReembolso && reembolsoTipo === 'externo' && !nombreExternoReembolso.trim()) return 'Por favor ingresa el nombre de la persona a reembolsar';
    return null;
  };

  const resetForm = () => {
    setCategoriaEgreso('general');
    setSubcategoria('');
    setOtraSubcategoria('');
    setMonto('');
    setFecha(hoyEnChile());
    setDescripcion('');
    setRequiereReembolso(false);
    setReembolsoTipo('miembro');
    setMiembroReembolsoId('');
    setNombreExternoReembolso('');
    setEsDonacion(false);
    setDonanteTipo('miembro');
    setMiembroDonante('');
    setNombreExternoDonante('');
  };

  const hasFormChanges = () =>
    descripcion !== '' || monto !== '' || subcategoria !== '' || requiereReembolso || esDonacion || miembroDonante !== '' || nombreExternoDonante !== '';

  return {
    categoriaEgreso, setCategoriaEgreso,
    subcategoria, setSubcategoria,
    otraSubcategoria, setOtraSubcategoria,
    monto, setMonto,
    fecha, setFecha,
    descripcion, setDescripcion,
    requiereReembolso, setRequiereReembolso,
    reembolsoTipo, setReembolsoTipo,
    miembroReembolsoId, setMiembroReembolsoId,
    nombreExternoReembolso, setNombreExternoReembolso,
    esDonacion,
    donanteTipo, setDonanteTipo,
    miembroDonante, setMiembroDonante,
    nombreExternoDonante, setNombreExternoDonante,
    subcategorias,
    reembolsoClassName,
    pillClasses,
    handleDonacionToggle,
    validateForm,
    resetForm,
    hasFormChanges,
  };
}
